/**
 * CreatorCta — one status-aware entry point into the creator program, reused
 * by nav menus, the profile page, the passport strip and map stop sheets.
 *
 * The label and destination always come from useCreatorStatus, so a user sees
 * the same next step wherever they are.
 */
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, BadgeCheck, Clock, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCreatorStatus } from "@/hooks/useCreatorStatus";
import { cn } from "@/lib/utils";

type Variant = "menu" | "card" | "inline";

interface Props {
  variant?: Variant;
  /** Optional override for the inline pitch line. */
  inlineLabel?: string;
  className?: string;
  onNavigate?: () => void;
}

function StatusIcon({ status, className }: { status: string; className?: string }) {
  if (status === "verified") return <BadgeCheck className={className} />;
  if (status === "pending") return <Clock className={className} />;
  return <Sparkles className={className} />;
}

export function CreatorCta({ variant = "inline", inlineLabel, className, onNavigate }: Props) {
  const navigate = useNavigate();
  const { status, label, menuLabel, statusLine, description, to, isLoading } = useCreatorStatus();

  if (isLoading) return null;

  const go = () => {
    onNavigate?.();
    navigate(to);
  };

  if (variant === "menu") {
    return (
      <button
        type="button"
        onClick={go}
        className={cn(
          "flex w-full items-center justify-between rounded-lg px-4 py-3 text-left transition-colors hover:bg-muted",
          className,
        )}
      >
        <span className="flex items-center gap-3">
          <StatusIcon status={status} className="h-5 w-5 shrink-0 text-primary" />
          <span className="flex min-w-0 flex-col">
            <span className="font-medium">{menuLabel}</span>
            <span className="truncate text-xs text-muted-foreground">{statusLine}</span>
          </span>
        </span>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </button>
    );
  }

  if (variant === "card") {
    return (
      <div
        className={cn(
          "rounded-xl border border-primary/25 bg-primary/5 p-4 space-y-2",
          className,
        )}
      >
        <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-primary">
          <StatusIcon status={status} className="h-3.5 w-3.5" /> Creator program
        </p>
        <p className="text-sm font-semibold leading-tight">{label}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
        <Button size="sm" onClick={go} className="mt-1 font-semibold">
          {status === "none" ? "Get started" : "Open"}
        </Button>
      </div>
    );
  }

  return (
    <Link
      to={to}
      onClick={onNavigate}
      className={cn(
        "inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline",
        className,
      )}
    >
      <StatusIcon status={status} className="h-3.5 w-3.5" />
      {inlineLabel && status === "none" ? inlineLabel : label}
    </Link>
  );
}
